import ICategory from "@models/ICategory";
import { FilterValues, IFilterField, IFilterSection, IFilterSelectOption } from "@models/IFilterConfig";

export interface IInventoryFilterLookups {
    /** Resolves a stored category guid (preset / deep link) to its "Parent > Child" path for the chip */
    resolveCategory?: (guid: string) => Promise<IFilterSelectOption | undefined>;
}

export interface IInventoryFilterContext {
    /**
     * A price group is selected on the screen. The legacy search only shows the price range inputs
     * then, because `getStockLevels` filters prices within the selected price group.
     */
    hasPriceGroup: boolean;
    /** Root categories of the selected catalog (the same list the legacy drill-down picker starts from) */
    categories: ICategory[];
    lookups: IInventoryFilterLookups;
    /** Translate a message id (used for static option labels) */
    formatMessage: (id: string) => string;
}

/** `IGetStockLevelsDTO.stock` values the FilterBar can select; the sentinel "" means `all`. */
export type InventoryStockFilter = "in_stock_only" | "out_of_stock_only";

const STOCK_VALUES: InventoryStockFilter[] = ["in_stock_only", "out_of_stock_only"];

/**
 * The query object the Inventory FilterBar emits. Keys are the `getStockLevels` payload fields, except the
 * price range: the socket body wants a nested `priceRange: { minPrice, maxPrice }`, which is not a valid
 * preset value, so the bar stores the two flat keys and the screen assembles the object.
 */
export interface IInventoryFilterQuery {
    name?: unknown;
    stock?: unknown;
    manufacturer?: unknown;
    categoryGuid?: unknown;
    minPrice?: unknown;
    maxPrice?: unknown;
}

/** Inventory screen state the query maps onto (the legacy search inputs). */
export interface IInventoryFilterState {
    name: string;
    manufacturer: string;
    stock: InventoryStockFilter | "all";
    categoryGuid: string | undefined;
    minPrice: number | null;
    maxPrice: number | null;
}

/**
 * Baseline Inventory filters: the legacy reset restores the supplier's `screens.inventory.out_of_stock_only`
 * setting and clears everything else.
 */
export function getInventoryDefaultFilters(outOfStockOnly: boolean): FilterValues {
    return outOfStockOnly ? { stock: "out_of_stock_only" } : {};
}

const toText = (value: unknown): string => (value === undefined || value === null ? "" : String(value));

/** Number inputs store strings; "" / non-numeric / 0 mean "no bound" (the legacy inputs are `null` then). */
const toPriceBound = (value: unknown): number | null => {
    if (value === undefined || value === null || value === "") {
        return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/** Map a FilterBar query onto the Inventory search state that `getStockLevels` reads. */
export function parseInventoryFilterQuery(query: IInventoryFilterQuery): IInventoryFilterState {
    const stock = toText(query.stock) as InventoryStockFilter;

    return {
        name: toText(query.name),
        manufacturer: toText(query.manufacturer),
        stock: STOCK_VALUES.includes(stock) ? stock : "all",
        categoryGuid: toText(query.categoryGuid) || undefined,
        minPrice: toPriceBound(query.minPrice),
        maxPrice: toPriceBound(query.maxPrice),
    };
}

/**
 * Compile the Inventory filter config. Field keys ARE the `getStockLevels` field names (see
 * IInventoryFilterQuery) so presets store what the screen sends. Catalog and price group are not
 * filters: they scope the whole screen (columns, cart, categories) and stay as screen-level selects.
 */
export function buildInventoryFilterSections(ctx: IInventoryFilterContext): IFilterSection[] {
    const { formatMessage } = ctx;

    const stockOptions: IFilterSelectOption[] = [
        { label: formatMessage("all"), value: "" },
        { label: formatMessage("in_stock_only"), value: "in_stock_only" },
        { label: formatMessage("out_of_stock_only"), value: "out_of_stock_only" },
    ];

    const search: IFilterField[] = [
        { key: "name", label: "name_sku_description", type: "text", apiKey: "name" },
        { key: "stock", label: "stock", type: "select", apiKey: "stock", options: stockOptions },
        { key: "manufacturer", label: "manufacturer", type: "text", apiKey: "manufacturer" },
        {
            key: "categoryGuid",
            label: "category",
            type: "category",
            apiKey: "categoryGuid",
            categories: ctx.categories,
            resolveOption: ctx.lookups.resolveCategory,
        },
    ];

    const priceRange: IFilterField[] = [];

    if (ctx.hasPriceGroup) {
        priceRange.push({ key: "minPrice", label: "price_range_start", type: "number", apiKey: "minPrice", min: 0, max: 1000000, step: 1 });
        priceRange.push({ key: "maxPrice", label: "price_range_end", type: "number", apiKey: "maxPrice", min: 0, max: 1000000, step: 1 });
    }

    return [
        { id: "search", title: "search", filters: search },
        { id: "priceRange", title: "price_range", filters: priceRange },
    ];
}
